/**
 * AssessmentTab - AI-generated strategic assessment placeholder
 * Shows expected structure for future AI assessment service
 */

import { memo } from 'react';

interface AssessmentTabProps {
  productId: string;
  productName: string;
}

function AssessmentTabComponent({
  productId: _productId, // Will be used when API is connected
  productName,
}: AssessmentTabProps) {
  // TODO: Connect to ProductAssessmentService API when available
  // For now, show a placeholder with the expected structure
  void _productId; // Silence unused warning - will be used for API call

  return (
    <div className="space-y-6">
      <div className="rounded-lg bg-amber-50 border border-amber-200 p-4">
        <div className="flex items-start gap-3">
          <svg
            className="h-5 w-5 text-amber-600 mt-0.5"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <div>
            <h4 className="text-sm font-medium text-amber-800">AI-Generated Assessment</h4>
            <p className="text-sm text-amber-700 mt-1">
              This strategic assessment is generated by AI and should be verified before use.
            </p>
          </div>
        </div>
      </div>

      <div className="rounded-lg border border-gray-200 p-4">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          Strategic Assessment: {productName}
        </h3>

        <div className="space-y-4">
          <section>
            <h4 className="text-sm font-medium text-gray-700 mb-2">Competitive Position</h4>
            <div className="text-sm text-gray-600 bg-gray-50 rounded p-3">
              Strategic assessment data will be generated when the AI assessment service is
              connected.
            </div>
          </section>

          <section>
            <h4 className="text-sm font-medium text-gray-700 mb-2">Recommended Response</h4>
            <div className="grid grid-cols-2 gap-2">
              {['Ignore', 'Monitor', 'Match', 'Leapfrog', 'Flank', 'Acquire'].map((option) => (
                <div
                  key={option}
                  className="rounded border border-gray-200 p-3 text-sm text-gray-500 text-center"
                >
                  {option}
                </div>
              ))}
            </div>
          </section>

          <section>
            <h4 className="text-sm font-medium text-gray-700 mb-2">Intelligence Gaps</h4>
            <ul className="text-sm text-gray-600 space-y-1">
              <li className="flex items-start gap-2">
                <span className="text-amber-500">*</span>
                Customer win/loss data needs verification
              </li>
              <li className="flex items-start gap-2">
                <span className="text-amber-500">*</span>
                Market share estimates are low confidence
              </li>
              <li className="flex items-start gap-2">
                <span className="text-amber-500">*</span>
                Pricing details unavailable
              </li>
            </ul>
          </section>
        </div>
      </div>
    </div>
  );
}

export const AssessmentTab = memo(AssessmentTabComponent);
